import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Crown, Check, BookOpen, Bookmark, Volume2, Loader2, Key, StickyNote, Highlighter, Bell, LogIn, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";

// Stripe public key
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/test_00g5kE5Ij9Wr9vq000"; // Remplacer par votre lien Stripe Payment Link

const PremiumPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const { user, isPremium, refreshPremium } = useAuth();

  const handleActivateCode = async () => {
    if (!user) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter pour activer Premium."
      });
      navigate('/connexion');
      return;
    }

    if (!secretCode.trim()) {
      toast.error("Veuillez entrer un code");
      return;
    }

    // Vérifier le code
    if (secretCode.trim().toUpperCase() !== 'FILLION1920') {
      toast.error("Code invalide", {
        description: "Vérifiez votre code et réessayez."
      });
      return;
    }
    
    setLoading(true);
    try {
      // Appeler la fonction Supabase pour valider le code avec limite de 100
      const { data, error } = await supabase.rpc('validate_fillion_code', {
        p_user_id: user.id
      });
      
      if (error) throw error;
      
      if (data.success) {
        // Rafraîchir le statut premium
        await refreshPremium();
        
        toast.success(data.message, {
          description: data.remaining !== undefined 
            ? `Places restantes : ${data.remaining}` 
            : "Vous avez maintenant accès à toutes les fonctionnalités."
        });
        setSecretCode("");
      } else {
        toast.error("Erreur", {
          description: data.message
        });
      }
    } catch (error) {
      console.error("Error activating code:", error);
      toast.error("Erreur d'activation", {
        description: "Une erreur s'est produite. Réessayez."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    if (!user) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter avant de payer."
      });
      navigate('/connexion');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://lpimrjarhgikirqedofj.supabase.co/functions/v1/stripe-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxwaW1yamFyaGdpa2lycWVkb2ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI5MDM4OTUsImV4cCI6MjA4ODQ3OTg5NX0.1AvhdRfm5AdDcknqAPlrw5r-8ByK-mhpNNFYAzTnhrM',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxwaW1yamFyaGdpa2lycWVkb2ZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI5MDM4OTUsImV4cCI6MjA4ODQ3OTg5NX0.1AvhdRfm5AdDcknqAPlrw5r-8ByK-mhpNNFYAzTnhrM',
        },
        body: JSON.stringify({ 
          userId: user.id, 
          email: user.email,
          successUrl: `${window.location.origin}/premium/success`,
          cancelUrl: `${window.location.origin}/premium`
        }),
      });

      const data = await response.json();

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'No checkout URL returned');
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Erreur de paiement", {
        description: "Une erreur s'est produite. Réessayez ou utilisez un code d'activation."
      });
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Bookmark,
      title: "Garde-pages",
      description: "Sauvegardez vos positions de lecture pour reprendre là où vous en étiez."
    },
    {
      icon: StickyNote,
      title: "Notes personnelles",
      description: "Ajoutez vos commentaires et réflexions sur chaque verset."
    },
    {
      icon: Highlighter,
      title: "Surlignage",
      description: "Surlignez les passages importants en 5 couleurs différentes."
    },
    {
      icon: Bell,
      title: "Rappels de lecture",
      description: "Recevez une notification quotidienne pour ne pas oublier de lire."
    },
    {
      icon: Volume2,
      title: "Lecture audio",
      description: "Écoutez la Bible lue par des voix élégantes, homme ou femme."
    }
  ];

  return (
    <div className="min-h-screen bg-paper animate-fade-in">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-leaf/20 mb-6">
            <Crown className="h-8 w-8 text-gold-leaf" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-ink-primary mb-4">
            Bible Fillion <span className="text-gold-leaf">Premium</span>
          </h1>
          <p className="font-body text-lg text-ink-secondary max-w-2xl mx-auto">
            Accédez aux fonctionnalités avancées : signets illimités et lecture audio.
          </p>
        </header>

        {isPremium ? (
          /* Already Premium */
          <Card className="bg-paper-dark border-gold-leaf border-2 mb-8">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <Check className="h-6 w-6 text-green-600" strokeWidth={2} />
              </div>
              <h2 className="font-heading text-2xl text-ink-primary mb-2">
                Vous êtes Premium !
              </h2>
              <p className="font-body text-ink-secondary mb-6">
                Profitez de toutes les fonctionnalités de la Bible Fillion.
              </p>
              <Button
                onClick={() => navigate("/bible/genese/1")}
                className="bg-ink-primary hover:bg-ink-secondary text-paper"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Commencer la lecture
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Activation Card */
          <Card className="bg-paper-dark border-border mb-8">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-3xl text-ink-primary">
                Activer Premium
              </CardTitle>
              <CardDescription className="font-body text-ink-secondary">
                {user ? "Choisissez votre méthode d'activation" : "Connectez-vous pour activer Premium"}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              {!user ? (
                /* Not logged in */
                <div className="text-center">
                  <p className="text-ink-secondary mb-6">
                    Vous devez être connecté pour activer Premium et sauvegarder vos données.
                  </p>
                  <Link to="/connexion">
                    <Button
                      size="lg"
                      className="bg-amber-800 hover:bg-amber-900 text-white font-body py-6 text-lg"
                    >
                      <LogIn className="h-5 w-5 mr-2" />
                      Se connecter
                    </Button>
                  </Link>
                </div>
              ) : (
                /* Logged in - Show payment options */
                <div className="max-w-md mx-auto space-y-6">
                  <p className="text-sm text-ink-secondary text-center mb-4">
                    Connecté en tant que <strong>{user.email}</strong>
                  </p>

                  {/* Stripe Payment */}
                  <div className="text-center">
                    <div className="mb-4">
                      <div className="text-4xl font-bold text-ink-primary mb-2">4,99€</div>
                      <p className="text-sm text-ink-secondary">Paiement unique - Accès à vie</p>
                    </div>
                    <Button
                      onClick={handleStripePayment}
                      disabled={loading}
                      size="lg"
                      className="w-full bg-ink-primary hover:bg-ink-secondary text-paper font-body py-6 text-lg"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="h-5 w-5 mr-2" />
                      )}
                      Payer avec Carte Bancaire
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-paper-dark px-2 text-ink-secondary">Ou</span>
                    </div>
                  </div>

                  {/* Code Activation */}
                  <div className="text-center">
                    <p className="text-sm text-ink-secondary mb-4">Vous avez un code d'activation ?</p>
                    <div className="flex gap-2 mb-3">
                      <Input
                        type="text"
                        placeholder="Code d'activation"
                        value={secretCode}
                        onChange={(e) => setSecretCode(e.target.value.toUpperCase())}
                        className="font-body text-center text-lg"
                        onKeyDown={(e) => e.key === 'Enter' && handleActivateCode()}
                      />
                    </div>
                    <Button
                      onClick={handleActivateCode}
                      disabled={loading}
                      variant="outline"
                      size="lg"
                      className="w-full border-gold-leaf text-gold-leaf hover:bg-gold-leaf/10 font-body py-6 text-lg"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <Key className="h-5 w-5 mr-2" />
                      )}
                      Activer avec le code
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="bg-paper border-border">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-paper-dark flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-gold-leaf" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-ink-primary mb-1">
                        {feature.title}
                      </h3>
                      <p className="font-body text-sm text-ink-secondary">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quote */}
        <div className="text-center py-8">
          <blockquote className="font-body text-lg text-ink-primary italic">
            « Notre but est de procurer au clergé, spécialement aux jeunes lévites et aux prêtres du ministère, un commentaire succinct de toute la Bible, écrit en langue française. »
          </blockquote>
          <cite className="font-body text-ink-secondary mt-4 block not-italic">
            — L.-Cl. Fillion, Avant-propos, 1887
          </cite>
        </div>
      </div>
    </div>
  );
};

export default PremiumPage;
